import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  apagarDocumentoBoraFazer,
  apagarEventoBoraFazer,
  apagarTarefaBoraFazer,
  baixarParcelaBoraFazer,
  criarEventoBoraFazer,
  criarTarefaBoraFazer,
  editarTarefaBoraFazer,
  getAcessoBoraFazer,
  getCatalogoIndicadores,
  getDocumentosBoraFazer,
  getIndicadoresBoraFazer,
  getParcelasBoraFazer,
  getRelatorioHorasBoraFazer,
  getResumoBoraFazer,
  getTarefasBoraFazer,
  getTimeBoraFazer,
  getTimelineBoraFazer,
  removerIndicadorBoraFazer,
  salvarDocumentoBoraFazer,
  salvarIndicadorBoraFazer,
  type BoraFazerDocumento,
  type BoraFazerTarefa,
  type NovoEvento,
} from '@/lib/boraFazerApi';

/** Quem enxerga o projeto: o time da Bora Fazer, o time da Prime no projeto e a gestão. */
export function useBoraFazerAcesso() {
  return useQuery({
    queryKey: ['bora-fazer', 'acesso'],
    queryFn: getAcessoBoraFazer,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useBoraFazerTime(enabled = true) {
  return useQuery({
    queryKey: ['bora-fazer', 'time'],
    queryFn: getTimeBoraFazer,
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * Os tiles do topo se refazem sozinhos: a ligação entra pelo ramal ~1 min depois de
 * desligar, e quem está com a página aberta na reunião não pode ver "0 ligações"
 * com duas na lista embaixo (foi o que aconteceu no primeiro teste).
 */
export function useBoraFazerResumo(enabled = true) {
  return useQuery({
    queryKey: ['bora-fazer', 'resumo'],
    queryFn: getResumoBoraFazer,
    enabled,
    staleTime: 15_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useBoraFazerTimeline(filtros: { tipo?: string; q?: string }, enabled = true) {
  return useQuery({
    queryKey: ['bora-fazer', 'timeline', filtros.tipo || 'todos', filtros.q || ''],
    queryFn: () => getTimelineBoraFazer(filtros),
    enabled,
    staleTime: 15_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useCriarEventoBoraFazer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (evento: NovoEvento) => criarEventoBoraFazer(evento),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bora-fazer', 'timeline'] });
      qc.invalidateQueries({ queryKey: ['bora-fazer', 'resumo'] });
    },
  });
}

export function useApagarEventoBoraFazer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apagarEventoBoraFazer(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bora-fazer', 'timeline'] });
      qc.invalidateQueries({ queryKey: ['bora-fazer', 'resumo'] });
    },
  });
}

// ── Plano de ação ────────────────────────────────────────────────────────────

export function useBoraFazerTarefas(filtros: { status?: string; tipo?: string }, enabled = true) {
  return useQuery({
    queryKey: ['bora-fazer', 'tarefas', filtros.status || 'todas', filtros.tipo || 'todos'],
    queryFn: () => getTarefasBoraFazer(filtros),
    enabled,
    staleTime: 15_000,
  });
}

export function useSalvarTarefaBoraFazer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dados }: { id?: string; dados: Partial<BoraFazerTarefa> }) =>
      id ? editarTarefaBoraFazer(id, dados) : criarTarefaBoraFazer(dados),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bora-fazer', 'tarefas'] }),
  });
}

export function useApagarTarefaBoraFazer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apagarTarefaBoraFazer(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bora-fazer', 'tarefas'] }),
  });
}

// ── Contrato: parcelas e relatório de horas ──────────────────────────────────

export function useBoraFazerParcelas(enabled = true) {
  return useQuery({
    queryKey: ['bora-fazer', 'parcelas'],
    queryFn: getParcelasBoraFazer,
    enabled,
    staleTime: 60_000,
  });
}

export function useBaixarParcelaBoraFazer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, pago_em, observacao }: { id: string; pago_em: string | null; observacao?: string | null }) =>
      baixarParcelaBoraFazer(id, { pago_em, observacao }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bora-fazer', 'parcelas'] }),
  });
}

export function useBoraFazerRelatorioHoras(competencia: string, enabled = true) {
  return useQuery({
    queryKey: ['bora-fazer', 'relatorio-horas', competencia],
    queryFn: () => getRelatorioHorasBoraFazer(competencia),
    enabled,
    staleTime: 30_000,
  });
}

// ── Indicadores ──────────────────────────────────────────────────────────────

export function useBoraFazerIndicadores(enabled = true) {
  return useQuery({
    queryKey: ['bora-fazer', 'indicadores'],
    queryFn: getIndicadoresBoraFazer,
    enabled,
    staleTime: 60_000,
  });
}

export function useCatalogoIndicadoresBoraFazer(enabled = true) {
  return useQuery({
    queryKey: ['bora-fazer', 'indicadores', 'catalogo'],
    queryFn: getCatalogoIndicadores,
    enabled,
    staleTime: 10 * 60_000,
  });
}

export function useSalvarIndicadorBoraFazer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ codigo, meta }: { codigo: string; meta: number | null }) => salvarIndicadorBoraFazer(codigo, meta),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bora-fazer', 'indicadores'] }),
  });
}

export function useRemoverIndicadorBoraFazer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => removerIndicadorBoraFazer(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bora-fazer', 'indicadores'] }),
  });
}

// ── Documentos vivos ─────────────────────────────────────────────────────────

export function useBoraFazerDocumentos(historico: boolean, enabled = true) {
  return useQuery({
    queryKey: ['bora-fazer', 'documentos', historico],
    queryFn: () => getDocumentosBoraFazer(historico),
    enabled,
    staleTime: 30_000,
  });
}

export function useSalvarDocumentoBoraFazer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (doc: Partial<BoraFazerDocumento> & { arquivo_path: string }) => salvarDocumentoBoraFazer(doc),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bora-fazer', 'documentos'] }),
  });
}

export function useApagarDocumentoBoraFazer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apagarDocumentoBoraFazer(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bora-fazer', 'documentos'] }),
  });
}
